import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import VariantDisplay from '../components/VariantDisplay';
import ProductOptionsDisplay from '../components/CustomOptionsDisplay';
import StarRating from '../components/StarRating';
import { 
  ArrowLeft, Edit, Trash2, Package, DollarSign, Truck, Calendar,
  ChevronLeft, ChevronRight, User, CheckCircle, Shield, AlertCircle,
  Clock, RefreshCw, Gift
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getImageUrl, getFirstImage } from '../utils/imageHelper';
import { getSubscriptionPrice, getSubscriptionCycleSuffix } from '../utils/subscriptionPrice';

const SubscriptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [subscription, setSubscription] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [variantSelections, setVariantSelections] = useState({});
  const [variantPriceAdjustment, setVariantPriceAdjustment] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  React.useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = sessionStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        } else {
          navigate('/listings/subscriptions');
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/listings/subscriptions');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchSubscription();
  }, [id, navigate]);
  
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;
    
    setDeleting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Subscription deleted successfully' });
        navigate('/listings/subscriptions');
      } else {
        toast({ title: 'Error', description: 'Failed to delete subscription', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error deleting subscription', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleVariantSelectionChange = ({ selections, priceAdjustment }) => {
    setVariantSelections(selections);
    setVariantPriceAdjustment(priceAdjustment || 0);
  };

  const nextImage = () => {
    if (subscription?.media?.length > 0) {
      setSelectedImage((prev) => (prev + 1) % subscription.media.length);
    }
  };

  const prevImage = () => {
    if (subscription?.media?.length > 0) {
      setSelectedImage((prev) => (prev - 1 + subscription.media.length) % subscription.media.length);
    }
  };

  const getDisplayPrice = () => {
    const basePrice = getSubscriptionPrice(subscription);
    // If custom options have calculated a price, use that as base
    if (calculatedPrice !== null) {
      return calculatedPrice + variantPriceAdjustment;
    }
    return basePrice + variantPriceAdjustment;
  };

  const handlePriceChange = (newPrice) => {
    setCalculatedPrice(newPrice);
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!subscription) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Subscription not found</div>
        </div>
      </DashboardLayout>
    );
  }
  
  const data = subscription.data || {};
  const images = subscription.media || [];
  const basePrice = getSubscriptionPrice(subscription);
  const cycleSuffix = getSubscriptionCycleSuffix(subscription) || '/mo';
  
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4">
          <Button variant="ghost" onClick={() => navigate('/listings/subscriptions')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Subscriptions
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/listings/subscriptions/edit/${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          
          {/* LEFT COLUMN - Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl overflow-hidden border aspect-square">
              {images.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(images[selectedImage])}
                    alt={subscription.title}
                    className="w-full h-full object-contain"
                  />
                  {images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Package className="h-24 w-24 text-gray-300" />
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((url, index) => (
                  <button
                    key={url.id || url.name || `${url.title || "url"}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${selectedImage === index ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <img src={getImageUrl(url)} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="included">What&apos;s Included</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{subscription.description}</p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="included" className="mt-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Gift className="h-5 w-5 text-green-500 mt-0.5" />
                      <p className="text-gray-700 whitespace-pre-wrap">{data.whatIncluded || 'Not specified'}</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="policies" className="mt-4">
                <Card>
                  <CardContent className="pt-4 space-y-4">
                    {data.cancellationPolicy && (
                      <div>
                        <h4 className="font-medium mb-2">Cancellation Policy</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.cancellationPolicy}</p>
                      </div>
                    )}
                    {data.minimumCommitment && (
                      <div>
                        <h4 className="font-medium mb-2">Minimum Commitment</h4>
                        <p className="text-sm text-gray-600">{data.minimumCommitment}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT COLUMN - Info & Configuration */}
          <div className="space-y-6">
            {/* Title & Status */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>{subscription.status}</Badge>
                {data.autoRenewal && <Badge className="bg-green-600">Auto-Renewal</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{subscription.title}</h1>
              <div className="mt-2">
                <StarRating listingId={id} size="md" showCount={true} fetchData={true} />
              </div>
              {basePrice > 0 && (
                <div className="mt-3 flex items-baseline gap-1" data-testid="subscription-base-price">
                  <span className="text-3xl font-bold text-gray-900">₹{basePrice.toLocaleString('en-IN')}</span>
                  <span className="text-base text-gray-600">{cycleSuffix}</span>
                </div>
              )}
            </div>

            {/* Vendor Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Provider</p>
                <p className="font-medium">{subscription.vendor_name || 'Provider'}</p>
              </div>
            </div>

            {/* Category & Type */}
            <div className="flex gap-2 flex-wrap">
              {data.subscriptionType && <Badge variant="outline">{data.subscriptionType}</Badge>}
              {subscription.category && <Badge variant="outline">{subscription.category}</Badge>}
            </div>

            {/* Subscription Plan Cards */}
            <div className="space-y-2" data-testid="subscription-plan-cards">
              <h3 className="text-sm font-semibold text-gray-700">Choose Your Plan</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Monthly',     key: 'monthlyPrice',    months: 1,  badge: null },
                  { label: 'Quarterly',   key: 'quarterlyPrice',  months: 3,  badge: 'Save' },
                  { label: 'Half-Yearly', key: 'halfYearlyPrice', months: 6,  badge: 'Save More' },
                  { label: 'Yearly',      key: 'yearlyPrice',     months: 12, badge: 'Best Value' },
                ].map(({ label, key, months, badge }) => {
                  const price = parseFloat(data[key] || 0);
                  if (!price) return null;
                  const monthly = parseFloat(data.monthlyPrice || data.base_plan_price || data.basePlanPrice || price);
                  const savePct = months > 1 && monthly > 0
                    ? Math.round((1 - price / (monthly * months)) * 100)
                    : 0;
                  return (
                    <div key={key}
                      className="relative border-2 border-gray-200 rounded-xl p-3 bg-white hover:border-indigo-400 hover:bg-indigo-50/30 transition-all cursor-default"
                      data-testid={`plan-card-${key}`}>
                      {badge && savePct > 0 && (
                        <span className="absolute -top-2 right-2 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
                          {savePct}% off
                        </span>
                      )}
                      <div className="font-semibold text-xs text-gray-500">{label}</div>
                      <div className="text-lg font-bold text-gray-900 mt-0.5">₹{price.toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-400">≈ ₹{Math.round(price / months).toLocaleString('en-IN')}/mo</div>
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>

            {/* Variants */}
            {(subscription.variants?.length > 0 || subscription.data?.variants?.length > 0) && (
              <VariantDisplay 
                variants={subscription.variants?.length > 0 ? subscription.variants : (subscription.data?.variants || [])}
                variantCombinations={subscription.variant_combinations?.length > 0 ? subscription.variant_combinations : (subscription.data?.variant_combinations || subscription.data?.variantCombinations || [])}
                title="Subscription Options" 
                compact={true}
                basePrice={basePrice}
                onSelectionChange={handleVariantSelectionChange}
              />
            )}

            {/* Custom Options Display - shown for all listing types */}
            {(subscription.has_custom_options || subscription.data?.hasCustomOptions || subscription.data?.customOptionsConfig?.options?.length > 0 || subscription.data?.productOptionsConfig?.options?.length > 0) && (
              <ProductOptionsDisplay 
                listingId={id}
                basePrice={basePrice}
                variantSelections={variantSelections}
                variantPriceAdjustment={variantPriceAdjustment}
                onPriceChange={handlePriceChange}
              />
            )}

            {/* Subscription Details */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                {data.deliveryFrequency && (
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Delivery: </span>
                      <span className="font-medium capitalize">{data.deliveryFrequency}</span>
                    </div>
                  </div>
                )}
                {data.trialPeriod && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Trial Period: </span>
                      <span className="font-medium">{data.trialPeriod} days</span>
                    </div>
                  </div>
                )}
                {data.autoRenewal !== undefined && (
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-500">Auto-Renewal: </span>
                      <span className="font-medium">{data.autoRenewal ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button className="w-full h-12 text-lg bg-gray-900 hover:bg-gray-800" disabled>
                <Package className="h-5 w-5 mr-2" />
                SUBSCRIBE NOW
              </Button>
              <Button variant="outline" className="w-full h-12 text-lg" disabled>
                <Gift className="h-5 w-5 mr-2" />
                GIFT THIS SUBSCRIPTION
              </Button>
              <p className="text-xs text-center text-gray-500">(Preview mode - Buttons disabled in admin view)</p>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <CheckCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Verified</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Secure</p>
              </div>
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-600">Support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionDetails;